import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n";
import { heroImage } from "../data";
import { scrollTo } from "./Nav";

function MaskedLine({ children, delay, className }) {
  return (
    <span className={`block overflow-hidden ${className || ""}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden pt-16 md:pt-20 pb-10">
      <div className="relative z-10 w-full px-5 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          data-testid="hero-kicker"
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-black/60 mb-8 md:mb-12"
        >
          {t.hero.kicker}
        </motion.p>

        <motion.h1
          style={{ y: textY }}
          data-testid="hero-title"
          className="font-serif text-[clamp(2.6rem,8vw,7.5rem)] leading-[0.98] tracking-tight md:max-w-[48%]"
        >
          <MaskedLine delay={0.15}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.3} className="relative w-fit">
            <span className="relative inline-block">{t.hero.line2}</span>
          </MaskedLine>
          <MaskedLine delay={0.45}>{t.hero.line3}</MaskedLine>
        </motion.h1>

        <div className="mt-10 md:mt-16 flex flex-col md:flex-row md:items-end md:justify-start gap-10 md:gap-20 md:max-w-[55%]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subtitle"
            className="max-w-md text-sm md:text-base leading-relaxed text-black/70"
          >
            {t.hero.sub}
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => scrollTo("#original")}
            data-testid="hero-cta"
            className="group w-fit flex items-center gap-3 border border-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-klein hover:border-klein hover:text-linen"
          >
            {t.hero.cta}
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
          </motion.button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.9, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block absolute right-10 top-32 bottom-10 z-0"
      >
        <div className="relative overflow-hidden border border-black/15 h-full aspect-[989/1591]">
          <img
            src={heroImage}
            alt={t.hero.caption}
            className="h-full w-full object-cover"
          />
        </div>
      </motion.div>
    </section>
  );
}
